import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SavingsIcon from '@mui/icons-material/Savings';

const formatCurrency = (value) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const expenseItems = [
  { name: 'Bookkeeping/Audit', actual: 475, proposed: 325 },
  { name: 'Easter Egg Hunt', actual: 275, proposed: 150 },
  { name: 'End of School Bash', actual: 414, proposed: 350 },
  { name: 'Halloween Bash', actual: 900, proposed: 250 },
  { name: 'Christmas Decorations', actual: 200, proposed: 0 },
  { name: 'Benches for Kids', actual: 350, proposed: 0 },
  { name: 'Mulch', actual: 1200, proposed: 1200 },
  { name: 'Calderon Services', actual: 595, proposed: 0 },
  { name: 'Front Lawn Cut', actual: 1305, proposed: 1400 },
  { name: 'Mighty Green', actual: 280, proposed: 300 },
  { name: 'Insurance', actual: 1334, proposed: 1334 },
  { name: 'USPS', actual: 50, proposed: 50 },
  { name: 'Utilities', actual: 2800, proposed: 3000 },
  { name: 'Shelby Co Lien', actual: 25, proposed: 0 },
  { name: 'Sherwin Williams', actual: 30, proposed: 0 },
  { name: 'Property Tax', actual: 600, proposed: 600 },
  { name: 'Lighting/Electrical Front Repairs', actual: 2349, proposed: 100 },
];

const actualTotal = expenseItems.reduce((sum, item) => sum + item.actual, 0);
const proposedTotal = expenseItems.reduce((sum, item) => sum + item.proposed, 0);

const startingBalance = 15748.85;
const duesCollected2025 = 7680;
const duesCollected2026 = 8085;
const totalAvailable2025 = startingBalance + duesCollected2025;
const endingBalance = totalAvailable2025 - actualTotal;

const SummaryCard = ({ icon, label, value, color }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flex: 1,
        minWidth: { xs: '100%', sm: 220 },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${color}14`,
          color: color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

const Budget = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Helmet>
        <title>2025-2026 HOA Budget | Brynleigh Estates</title>
        <meta name="description" content="Brynleigh Estates HOA budget breakdown showing 2025 actual expenses and the proposed 2026 budget for dues and community spending." />
        <link rel="canonical" href="https://brynleighestates.com/budget" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/budget" />
        <meta property="og:title" content="2025-2026 HOA Budget | Brynleigh Estates" />
        <meta property="og:description" content="Brynleigh Estates HOA budget breakdown showing 2025 actual expenses and the proposed 2026 budget for dues and community spending." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="2025-2026 HOA Budget | Brynleigh Estates" />
        <meta name="twitter:description" content="Brynleigh Estates HOA budget breakdown showing 2025 actual expenses and the proposed 2026 budget for dues and community spending." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>

      <Hero
        imageUrl="/images/hills-bg.jpeg"
        title="HOA Budget"
        subtitle="Financial overview for Brynleigh Estates homeowners"
      />

      <Container maxWidth="lg">
        <Box sx={{ my: 6 }}>
          {/* Summary Cards */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 5,
            }}
          >
            <SummaryCard
              icon={<AccountBalanceIcon />}
              label="Starting Balance (Feb 2025)"
              value={formatCurrency(startingBalance)}
              color={theme.palette.primary.main}
            />
            <SummaryCard
              icon={<TrendingUpIcon />}
              label="2025 Dues Collected"
              value={formatCurrency(duesCollected2025)}
              color={theme.palette.success.main}
            />
            <SummaryCard
              icon={<TrendingDownIcon />}
              label="2025 Total Expenses"
              value={formatCurrency(actualTotal)}
              color={theme.palette.error.main}
            />
            <SummaryCard
              icon={<SavingsIcon />}
              label="Ending Balance"
              value={formatCurrency(endingBalance)}
              color={theme.palette.info.main}
            />
          </Box>

          {/* 2025 Actuals vs 2026 Proposed */}
          <Typography variant="h4" gutterBottom fontWeight="medium">
            Expense Breakdown
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Comparison of actual expenses from Feb 2025 &ndash; Jan 2026 and proposed budget for Feb 2026 &ndash; Jan 2027.
          </Typography>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mb: 4 }}
          >
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Expense</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    2025 Actual
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    2026 Proposed
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Change
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenseItems.map((item, index) => {
                  const diff = item.proposed - item.actual;
                  return (
                    <TableRow
                      key={item.name}
                      sx={{
                        bgcolor: index % 2 === 0 ? 'transparent' : 'action.hover',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                      <TableCell align="right">{formatCurrency(item.actual)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.proposed)}</TableCell>
                      <TableCell align="right">
                        {diff === 0 ? (
                          <Chip label="No change" size="small" variant="outlined" />
                        ) : (
                          <Chip
                            label={`${diff > 0 ? '+' : ''}${formatCurrency(diff)}`}
                            size="small"
                            color={diff < 0 ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals Row */}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Total Expenses</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {formatCurrency(actualTotal)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {formatCurrency(proposedTotal)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    <Chip
                      label={`${proposedTotal - actualTotal > 0 ? '+' : ''}${formatCurrency(proposedTotal - actualTotal)}`}
                      size="small"
                      color={proposedTotal - actualTotal < 0 ? 'success' : 'warning'}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Revenue Section */}
          <Typography variant="h4" gutterBottom fontWeight="medium">
            Dues Collected
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mb: 4 }}
          >
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Item</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    2025 Actual
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    2026 Projected
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Dues Collected</TableCell>
                  <TableCell align="right">{formatCurrency(duesCollected2025)}</TableCell>
                  <TableCell align="right">{formatCurrency(duesCollected2026)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 500 }}>Insurance Reimbursement</TableCell>
                  <TableCell align="right">{formatCurrency(757)}</TableCell>
                  <TableCell align="right">&mdash;</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Notes */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="medium">
              Budget Notes
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              The 2025 fiscal year ran from February 1, 2025 through January 31, 2026. The 2026 proposed budget covers February 1, 2026 through January 31, 2027.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Lighting and electrical front repairs in 2025 included quotes of $6,495 and $9,050 for reference, with $2,349 spent during the period.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The proposed 2026 budget reflects a reduction in total expenses from {formatCurrency(actualTotal)} to {formatCurrency(proposedTotal)}, a savings of {formatCurrency(actualTotal - proposedTotal)}. Projected dues collected increase from {formatCurrency(duesCollected2025)} to {formatCurrency(duesCollected2026)}.
            </Typography>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default Budget;
